import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import ApprovalItemsStatus, {
  readItemLabels,
} from '@/components/Approval/@components/ApprovalItemsStatus';
import {
  ClaimClosingSnapshot,
  countClaimClosingCards,
  type ClaimClosingDetailSource,
  type ClaimClosingTarget,
} from '@/components/Approval/@components/ClaimClosing';
import usePartialReject, {
  getPartialRejectErrorMessage,
} from '@/hooks/approval/usePartialReject';
import useApprovalView from '@/hooks/approval/useApprovalView';
import { useSnackbar } from '@/hooks/global-tools';
import { MyInfoState } from '@/recoil/atom';
import {
  CLAIM_CLOSING_REJECT_REASON_MAX_LENGTH,
  parseClaimClosingContent,
} from '@/utils/approval/claimClosing';
import { isCurrentApproverMeOf } from '../../actionVisibility';
import ReasonInputModal from '../../ReasonInputModal';
import type { ContentExtraProps } from '../../types';
import { ContentSlotRoot } from '../styles';

/**
 * 186 GERP 클레임 마감 — 저장 카드 HTML 그대로(레거시 문서 포함), 「상세보기」만 문서번호로 GERP 현재 값 조회.
 * 부분반려 버튼: 진행중 + 지금 내 결재 차례 + 카드 2장 이상(레거시 조건). 「추가 확인」은 결재 건별 현재 상태
 */
const ClaimClosingSlot = ({ data }: ContentExtraProps) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const myInfo = useAtomValue(MyInfoState);
  const { data: view } = useApprovalView(data.documentId);
  const { partialReject, submitting } = usePartialReject(data.documentId);
  // 닫히는 동안 제목이 비지 않게 대상은 남겨 둠
  const [target, setTarget] = useState<ClaimClosingTarget | null>(null);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const content = parseClaimClosingContent(data.attributes);

  const detailSource = useMemo<ClaimClosingDetailSource>(
    () => ({ kind: 'document', documentId: data.documentId }),
    [data.documentId],
  );
  const docTits = useMemo(
    () => readItemLabels(content, [['data-aprv_id', 'data-aprv_doctit']]),
    [content],
  );

  const canPartialReject =
    !!view &&
    view.status === 'P' &&
    isCurrentApproverMeOf(
      view.approvers,
      view.currentApproverOrder,
      myInfo?.passportId ?? '',
      view.status,
    ) &&
    countClaimClosingCards(content) >= 2;

  const renderCardAction = useCallback(
    (card: ClaimClosingTarget) =>
      canPartialReject ? (
        <BoxButton
          key={`reject-${card.itemId}`}
          type="button"
          variant="secondary"
          size="small"
          onClick={() => {
            setTarget(card);
            setIsRejectOpen(true);
          }}
        >
          {t('부분반려')}
        </BoxButton>
      ) : null,
    [canPartialReject, t],
  );

  const handleSubmit = async (reason: string) => {
    if (!target) return;
    try {
      await partialReject(target.itemId, reason);
      setIsRejectOpen(false);
      showSuccess({ message: t('부분 반려가 완료 되었습니다.') });
    } catch (error) {
      showError({ message: t(getPartialRejectErrorMessage(error)) });
    }
  };

  if (!content) return null;
  return (
    <ContentSlotRoot>
      <ApprovalItemsStatus
        documentId={data.documentId}
        buttonLabel="추가 확인"
        title="내역확인"
        labelHeader="No"
        portalRecordedStatus
        labels={docTits}
      />
      <ClaimClosingSnapshot
        content={content}
        detailSource={detailSource}
        renderCardAction={renderCardAction}
      />
      <ReasonInputModal
        open={isRejectOpen}
        onClose={() => setIsRejectOpen(false)}
        onSubmit={handleSubmit}
        submitting={submitting}
        title={t('{{docTit}} 반려', { docTit: target?.docTit ?? '' })}
        submitLabel={t('반려처리')}
        reasonLabel={t('사유입력')}
        reasonPlaceholder=""
        reasonMaxLength={CLAIM_CLOSING_REJECT_REASON_MAX_LENGTH}
      />
    </ContentSlotRoot>
  );
};

export default ClaimClosingSlot;
